import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import ArticleCard from '@/components/feature/ArticleCard';
import SEOHead from '@/components/feature/SEOHead';
import { getFAQPageSchema, getWebSiteSchema, getBreadcrumbSchema, getOrganizationSchema } from '@/components/feature/SchemaOrg';
import { categories, getLatestArticles } from '@/mocks/articles';

const faqItems = [
  {
    question: '龟头责是什么病？严重吗？',
    answer: '龟头责即龟头炎（Balanitis），是男性龟头部因感染、刺激或过敏等原因引起的炎症反应。大多数龟头责经过规范治疗可以完全康复，不属于严重疾病，但如果不及时治疗，可能反复发作或引起并发症。',
  },
  {
    question: '龟头责会自愈吗？需要看医生吗？',
    answer: '轻微的龟头责在保持局部清洁干燥后有可能自行缓解，但建议出现症状后及时就医。未经规范治疗容易迁延不愈或转为慢性，反复发作还可能增加包皮瘢痕形成等风险。',
  },
  {
    question: '龟头责治疗需要多长时间？',
    answer: '治疗周期因病因和严重程度而异：轻度念珠菌感染局部用药1-2周；细菌感染通常需5-7天；慢性或反复发作者可能需要2-4周。糖尿病患者治疗周期可能更长，需同时控制血糖。',
  },
  {
    question: '如何预防龟头责复发？',
    answer: '预防要点包括：每天清洗龟头保持清洁干燥、选择宽松透气内裤、避免使用刺激性清洁产品、性生活注意卫生、控制血糖（糖尿病患者）、包皮过长反复发作者可考虑包皮环切术。',
  },
  {
    question: '龟头责会传染给性伴侣吗？',
    answer: '感染性龟头责（如念珠菌性、滴虫性）可通过性接触传染给伴侣，建议治疗期间避免性生活或使用安全套。非感染性（如过敏、刺激引起）一般不具传染性。如果伴侣同时有阴道炎症状，应双方同时治疗。',
  },
  {
    question: '什么情况下需要做包皮手术？',
    answer: '包茎者、包皮过长且每年反复发作龟头责3次以上者、包皮过长伴有反复尿路感染者、发生过包皮嵌顿者，建议在炎症消退后进行包皮环切术。该手术可降低80%以上的复发风险。',
  },
];

const symptomCards = [
  { icon: 'ri-alert-line', title: '龟头红肿', desc: '龟头局部或弥漫性发红、肿胀，触碰时有疼痛感' },
  { icon: 'ri-contrast-drop-line', title: '瘙痒灼热', desc: '龟头区域出现瘙痒感和灼热刺痛，夜间可能加重' },
  { icon: 'ri-bug-line', title: '异常分泌物', desc: '包皮内板出现白色或黄白色分泌物，可能有异味' },
  { icon: 'ri-surgical-mask-line', title: '皮疹糜烂', desc: '龟头表面出现红色丘疹、水疱或浅表糜烂面' },
  { icon: 'ri-emotion-unhappy-line', title: '排尿不适', desc: '炎症累及尿道口时出现尿频、尿急、尿痛' },
  { icon: 'ri-mental-health-line', title: '包皮红肿', desc: '包皮同时受累，出现包皮红肿、紧缩感' },
];

export default function HomePage() {
  const latestArticles = getLatestArticles(6);

  return (
    <>
      <SEOHead
        title="龟头责的症状、原因与治疗方法全解析 - 男性生殖健康科普"
        description="龟头责是男性常见的生殖器官炎症问题，本页详细介绍龟头责的典型症状、常见致病原因、诊断方式及有效治疗方案，帮助男性正确认识并及时处理龟头责，保护生殖健康。"
        keywords="龟头责,龟头责症状,龟头责治疗,龟头责原因,龟头责预防,龟头责用药,男性生殖器炎症"
        canonicalUrl="https://www.nanke-health.com"
        ogType="website"
      />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(getOrganizationSchema()) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(getWebSiteSchema()) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(getBreadcrumbSchema([{ name: '首页', url: 'https://www.nanke-health.com' }])) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(getFAQPageSchema(faqItems)) }} />

      <div className="min-h-screen bg-background-50">
        <Navbar />

        {/* Hero Section */}
        <section className="relative w-full min-h-[580px] md:min-h-[680px] flex items-center justify-center overflow-hidden">
          <div className="absolute inset-0">
            <img
              src="https://readdy.ai/api/search-image?query=Soft%20warm%20abstract%20medical%20background%20with%20gentle%20gradients%20of%20cream%20and%20beige%20tones%2C%20minimalist%20healthcare%20concept%20with%20soft%20organic%20shapes%20and%20diffused%20natural%20lighting%2C%20clean%20medical%20aesthetic%20with%20calm%20and%20trustworthy%20atmosphere%2C%20professional%20yet%20approachable%20design%20with%20subtle%20texture&width=1920&height=900&seq=hero-home-01&orientation=landscape"
              alt="男性健康科普"
              className="w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/20 to-black/40"></div>
          </div>
          <div className="relative z-10 w-full px-4 md:px-6 py-20 text-center">
            <div className="max-w-3xl mx-auto">
              <span className="inline-block px-3 py-1.5 text-sm font-medium bg-accent-500/90 text-background-50 dark:text-foreground-950 rounded-full mb-6 whitespace-nowrap">
                专业男性生殖健康科普平台
              </span>
              <h1 className="font-heading text-3xl md:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
                龟头责的症状、原因与治疗
                <br />
                <span className="text-accent-300">全方位解析</span>
              </h1>
              <p className="text-base md:text-lg text-white/80 max-w-2xl mx-auto mb-8 leading-relaxed">
                龟头责（龟头炎）是男性常见的生殖器官炎症问题。我们提供权威的症状识别、病因分析、治疗方案和预防指导，帮助您正确认识并及时处理，守护男性生殖健康。
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                <Link
                  to="/category/zhengzhuang"
                  className="px-6 py-3 bg-primary-500 text-background-50 dark:text-foreground-950 font-medium rounded-md hover:bg-primary-600 transition-colors duration-200 whitespace-nowrap"
                >
                  <i className="ri-stethoscope-line mr-2"></i>
                  了解症状
                </Link>
                <Link
                  to="/category/zhiliao"
                  className="px-6 py-3 bg-white/15 text-white border border-white/30 font-medium rounded-md hover:bg-white/25 transition-colors duration-200 backdrop-blur-sm whitespace-nowrap"
                >
                  <i className="ri-heart-pulse-line mr-2"></i>
                  查看治疗方案
                </Link>
              </div>
            </div>
          </div>
          <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
            <i className="ri-arrow-down-line text-white/60 text-2xl"></i>
          </div>
        </section>

        {/* Quick Symptom Check */}
        <section className="py-14 md:py-20 bg-background-50">
          <div className="w-full px-4 md:px-6 max-w-7xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="font-heading text-2xl md:text-4xl font-bold text-foreground-950 mb-4">
                龟头责常见症状速查
              </h2>
              <p className="text-foreground-600 max-w-2xl mx-auto">
                及早识别龟头责的症状，是及时治疗的关键。以下是临床上最常见的六大典型症状，如发现类似表现，建议尽早就医。
              </p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {symptomCards.map((card, index) => (
                <div key={index} className="bg-background-100 border border-background-200/70 rounded-lg p-5 hover:border-primary-200/60 transition-colors duration-300">
                  <div className="w-10 h-10 flex items-center justify-center bg-accent-100 text-accent-600 rounded-lg mb-4">
                    <i className={`${card.icon} text-lg`}></i>
                  </div>
                  <h3 className="font-heading text-base font-semibold text-foreground-950 mb-2">{card.title}</h3>
                  <p className="text-sm text-foreground-600 leading-relaxed">{card.desc}</p>
                </div>
              ))}
            </div>
            <div className="text-center mt-8">
              <Link
                to="/category/zhengzhuang"
                className="inline-flex items-center gap-2 text-sm font-medium text-primary-600 hover:text-primary-700 transition-colors whitespace-nowrap"
              >
                查看全部症状详解
                <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
          </div>
        </section>

        {/* Category Navigation */}
        <section className="py-14 md:py-20 bg-background-100">
          <div className="w-full px-4 md:px-6 max-w-7xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="font-heading text-2xl md:text-4xl font-bold text-foreground-950 mb-4">
                按分类浏览文章
              </h2>
              <p className="text-foreground-600 max-w-2xl mx-auto">
                我们按照症状、原因、治疗、预防、用药五大板块整理了大量专业科普文章，便于您快速查找所需信息。
              </p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className="group bg-background-50 border border-background-200/70 rounded-lg p-6 text-center hover:border-primary-200/60 hover:bg-background-50 transition-all duration-300"
                >
                  <div className="w-14 h-14 md:w-16 md:h-16 mx-auto flex items-center justify-center bg-secondary-100 text-secondary-600 rounded-xl mb-4 group-hover:bg-primary-100 group-hover:text-primary-600 transition-colors duration-300">
                    <i className={`${cat.icon} text-2xl`}></i>
                  </div>
                  <h3 className="font-heading text-base font-semibold text-foreground-950 mb-2">{cat.name}</h3>
                  <p className="text-xs text-foreground-500 mb-3 leading-relaxed">{cat.description}</p>
                  <span className="text-xs font-medium text-primary-600 group-hover:text-primary-700 transition-colors whitespace-nowrap">
                    {cat.articleCount} 篇文章
                  </span>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* Latest Articles */}
        <section className="py-14 md:py-20 bg-background-50">
          <div className="w-full px-4 md:px-6 max-w-7xl mx-auto">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-10 gap-4">
              <div>
                <h2 className="font-heading text-2xl md:text-4xl font-bold text-foreground-950 mb-2">
                  最新科普文章
                </h2>
                <p className="text-foreground-600 text-sm">持续更新龟头责相关的专业医学知识</p>
              </div>
              <Link
                to="/category/zhiliao"
                className="inline-flex items-center gap-2 text-sm font-medium text-primary-600 hover:text-primary-700 transition-colors whitespace-nowrap"
              >
                浏览全部文章
                <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {latestArticles.map((article) => (
                <ArticleCard key={article.slug} article={article} />
              ))}
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-14 md:py-20 bg-background-100">
          <div className="w-full px-4 md:px-6 max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="font-heading text-2xl md:text-4xl font-bold text-foreground-950 mb-4">
                常见问题解答
              </h2>
              <p className="text-foreground-600">关于龟头责，您可能关心的常见问题</p>
            </div>
            <div className="space-y-3">
              {faqItems.map((faq, index) => (
                <details key={index} className="group bg-background-50 border border-background-200/70 rounded-lg overflow-hidden">
                  <summary className="flex items-center justify-between px-5 py-4 cursor-pointer hover:bg-background-100 transition-colors">
                    <span className="font-heading text-sm md:text-base font-medium text-foreground-900 pr-4">
                      {faq.question}
                    </span>
                    <i className="ri-add-line text-foreground-500 group-open:hidden flex-shrink-0 text-lg"></i>
                    <i className="ri-subtract-line text-foreground-500 hidden group-open:block flex-shrink-0 text-lg"></i>
                  </summary>
                  <div className="px-5 pb-4">
                    <p className="text-sm text-foreground-600 leading-relaxed">{faq.answer}</p>
                  </div>
                </details>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-14 md:py-20 bg-background-50">
          <div className="w-full px-4 md:px-6 max-w-3xl mx-auto text-center">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-4">
              关注男性健康，从科普开始
            </h2>
            <p className="text-foreground-600 mb-8 max-w-xl mx-auto">
              龟头责是可防可治的常见疾病。通过科学的知识普及和正确的健康管理，每位男性都可以有效保护自己的生殖健康。
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link
                to="/category/yufang"
                className="px-6 py-3 bg-primary-500 text-background-50 dark:text-foreground-950 font-medium rounded-md hover:bg-primary-600 transition-colors duration-200 whitespace-nowrap"
              >
                <i className="ri-shield-check-line mr-2"></i>
                学习预防知识
              </Link>
              <Link
                to="/about"
                className="px-6 py-3 border border-foreground-300 text-foreground-700 font-medium rounded-md hover:bg-background-100 transition-colors duration-200 whitespace-nowrap"
              >
                <i className="ri-information-line mr-2"></i>
                了解更多
              </Link>
            </div>
          </div>
        </section>

        <Footer />
        <ScrollToTop />
      </div>
    </>
  );
}